/**
 * FILE: apps/hub/src/lib/authz.ts
 * PURPOSE: Permission keys, route guards, and access control logic.
 * AGENT_CONTEXT: CRITICAL — Add new permissions here. Update PERMISSION_ROUTES for new pages.
 * INVARIANTS: super_admin bypasses most checks. First match wins in canAccess().
 * LAST_REVIEWED: 2026-05-18
 */

/** Map permission keys to allowed route prefixes (first match wins in canAccess). */
export const PERMISSION_ROUTES: Record<string, string[]> = {
  dashboard: ['/dashboard', '/overview', '/notifications', '/docs', '/settings'],
  students: ['/students', '/docs', '/settings'],
  students_read: ['/students', '/docs', '/settings'],
  enroll: ['/enroll', '/docs', '/settings'],
  edit_student: ['/edit-student', '/extend', '/override', '/docs', '/settings'],
  placements: ['/placements', '/docs', '/settings'],
  analytics: ['/analytics', '/docs', '/settings'],
  settings: ['/settings', '/docs'],
  activity: ['/activity', '/docs', '/settings'],
  support: ['/support/community-links', '/support/outreach', '/support/chatbot', '/students', '/docs', '/settings'],
}

function matchesPath(pathname: string, allowedPath: string): boolean {
  return pathname === allowedPath || pathname.startsWith(`${allowedPath}/`)
}

/** `/edit-student` and `/students/:phone/edit` — not covered by students_read prefix alone. */
const STUDENT_EDIT_PATH_RE = /^\/students\/[^/]+\/edit$/

export function isStudentEditPath(pathname: string): boolean {
  return pathname === '/edit-student' || STUDENT_EDIT_PATH_RE.test(pathname)
}

function canAccessStudentEdit(permissions: string[], roleName?: string): boolean {
  if (isSuperAdminRoleName(roleName)) return true
  return (
    permissions.includes('edit_student') ||
    permissions.includes('students') ||
    permissions.includes('enroll')
  )
}

export function isSuperAdminRoleName(roleName: string | undefined): boolean {
  return roleName === 'super_admin'
}

/** Website sync ops console — super_admin only. */
export function canAccessWebsiteSyncConsole(roleName?: string): boolean {
  return isSuperAdminRoleName(roleName)
}

export function hasPermission(permissions: string[], key: string, roleName?: string): boolean {
  if (isSuperAdminRoleName(roleName)) return true
  return permissions.includes(key)
}

/** Full student mutations (not read-only placements/CEO browsing). */
export function canMutateStudents(permissions: string[], roleName?: string): boolean {
  if (isSuperAdminRoleName(roleName)) return true
  if (permissions.includes('students')) return true
  if (permissions.includes('enroll') || permissions.includes('edit_student')) return true
  return false
}

export function canExportStudentsCsv(permissions: string[], roleName?: string): boolean {
  return hasPermission(permissions, 'analytics', roleName)
}

export function canAccess(permissions: string[], pathname: string, roleName?: string): boolean {
  if (matchesPath(pathname, '/admin/website-sync')) {
    return canAccessWebsiteSyncConsole(roleName)
  }
  if (isSuperAdminRoleName(roleName)) return true
  if (!permissions || permissions.length === 0) return false
  if (isStudentEditPath(pathname)) {
    return canAccessStudentEdit(permissions, roleName)
  }
  return permissions.some((perm) => {
    const routes = PERMISSION_ROUTES[perm] ?? []
    return routes.some((route) => matchesPath(pathname, route))
  })
}

const DEFAULT_PATH_PRIORITY = [
  '/dashboard',
  '/students',
  '/placements',
  '/support/community-links',
  '/enroll',
  '/edit-student',
  '/analytics',
  '/activity',
  '/settings',
] as const

export function defaultPathByPermissions(permissions: string[], roleName?: string): string {
  for (const path of DEFAULT_PATH_PRIORITY) {
    if (canAccess(permissions, path, roleName)) return path
  }
  return '/dashboard'
}
